// src/components/SharePresentationModal.jsx
import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../context/AuthContext';
import '../styles/SharePresentationModal.css';
import { useTranslation } from 'react-i18next';

const SharePresentationModal = ({ collection, onClose }) => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [expiresIn, setExpiresIn] = useState(24); // heures
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const qrCanvasRef = useRef(null);

  // Générer le QR code quand le lien change
  useEffect(() => {
    if (shareLink && qrCanvasRef.current) {
      QRCode.toCanvas(
        qrCanvasRef.current,
        shareLink.url,
        {
          width: 200,
          margin: 2,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        },
        (error) => {
          if (error) console.error('Erreur QR code:', error);
        }
      );
    }
  }, [shareLink]);

  // Générer le lien de partage (VERSION SESSION TOKEN)
  const generateShareLink = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculer la date d'expiration
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + expiresIn);

      console.log('[SharePresentation] Création lien avec session token');

      // Appeler la fonction SQL simplifiée
      const { data, error: createError } = await supabase
        .rpc('create_presentation_link', {
          p_collection_id: collection.id,
          p_created_by: user.id,
          p_expires_at: expiresAt.toISOString()
        });

      if (createError) {
        console.error('[SharePresentation] Erreur RPC:', createError);
        throw createError;
      }

      if (!data || data.length === 0) {
        throw new Error('empty_share_link');
      }

      const linkData = data[0];

      console.log('[SharePresentation] Lien créé:', linkData);

      // Construire l'URL avec le session_token
      const baseUrl = window.location.origin;
      const fullUrl = `${baseUrl}/presentation/shared/${linkData.session_token}`;

      setShareLink({
        url: fullUrl,
        share_token: linkData.share_token,
        session_token: linkData.session_token,
        expiresAt: new Date(linkData.expires_at)
      });

    } catch (err) {
      console.error('[SharePresentation] Erreur:', err);
      setError(t(err.message === 'empty_share_link' ? 'sharePresentationModal.errors.empty' : 'sharePresentationModal.errors.create'));
    } finally {
      setLoading(false);
    }
  };

  // Copier le lien
  const copyToClipboard = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Révoquer le lien
  const revokeLink = async () => {
    if (!shareLink) return;
    
    try {
      setLoading(true);
      
      const { error: revokeError } = await supabase
        .rpc('revoke_presentation_link', {
          p_share_token: shareLink.share_token
        });

      if (revokeError) throw revokeError;

      setShareLink(null);
      setError(null);
      
    } catch (err) {
      console.error('Erreur révocation:', err);
      setError(t('sharePresentationModal.errors.revoke'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="share-modal-header">
          <h2>{t('sharePresentationModal.title')}</h2>
          <button className="share-modal-close" onClick={onClose} aria-label={t('sharePresentationModal.close')}>
            ×
          </button>
        </div>

        <div className="share-modal-body">
          {error && (
            <div className="share-error">
              <p>{error}</p>
            </div>
          )}

          {!shareLink ? (
            // Formulaire de génération
            <div className="share-form">
              <p className="share-description">
                {t('sharePresentationModal.description')}
              </p>

              <div className="share-expiry-selector">
                <label>{t('sharePresentationModal.expiryLabel')}</label>
                <select 
                  value={expiresIn}
                  onChange={(e) => setExpiresIn(Number(e.target.value))}
                  className="share-select"
                >
                  <option value={1}>{t('sharePresentationModal.duration.hour_one', { count: 1 })}</option>
                  <option value={3}>{t('sharePresentationModal.duration.hour_other', { count: 3 })}</option>
                  <option value={6}>{t('sharePresentationModal.duration.hour_other', { count: 6 })}</option>
                  <option value={12}>{t('sharePresentationModal.duration.hour_other', { count: 12 })}</option>
                  <option value={24}>{t('sharePresentationModal.duration.hour_other', { count: 24 })}</option>
                  <option value={48}>{t('sharePresentationModal.duration.hour_other', { count: 48 })}</option>
                  <option value={72}>{t('sharePresentationModal.duration.day_other', { count: 3 })}</option>
                  <option value={168}>{t('sharePresentationModal.duration.day_other', { count: 7 })}</option>
                </select>
              </div>

              <button 
                onClick={generateShareLink}
                className="share-button-primary"
                disabled={loading}
              >
                {loading ? t('sharePresentationModal.generating') : t('sharePresentationModal.generate')}
              </button>
            </div>
          ) : (
            // Affichage du lien + QR
            <div className="share-result">
              <div className="share-qr-container">
                <canvas ref={qrCanvasRef} />
                <p className="share-qr-label">
                  {t('sharePresentationModal.scanMobile')}
                </p>
              </div>

              <div className="share-link-box">
                <input 
                  type="text"
                  value={shareLink.url}
                  readOnly
                  className="share-link-input"
                />
                <button 
                  onClick={copyToClipboard}
                  className="share-copy-button"
                >
                  {copied ? t('sharePresentationModal.copied') : t('sharePresentationModal.copy')}
                </button>
              </div>

              <div className="share-info">
                <p>
                  <strong>{t('sharePresentationModal.expiresOn')}</strong>{' '}
                  {shareLink.expiresAt.toLocaleString(i18n.resolvedLanguage || i18n.language || 'fr', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
                <p style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
                  {t('sharePresentationModal.directAccess')}
                </p>
              </div>

              <div className="share-actions">
                <button
                  onClick={revokeLink}
                  className="share-button-revoke"
                  disabled={loading}
                >
                  {t('sharePresentationModal.revoke')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SharePresentationModal;